import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { downloadAsJpeg } from 'Cloud/Application/DownloadAsJpeg';
import classNames from 'clsx';
import React, { memo, useCallback, useMemo, useState, VFC } from 'react';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { STORAGE_CONFIG } from 'reactApp/modules/storage/storage.config';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { Avatar } from 'reactApp/ui/Avatar/Avatar';
import { DataListCheckbox } from 'reactApp/ui/DataListCheckbox/DataListCheckbox';
import { DataListControl } from 'reactApp/ui/DataListControl/DataListControl';
import { Progress } from 'reactApp/ui/DataListItemRow/components/Progress';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { Tip } from 'reactApp/ui/Tip/Tip';
import { formatAutoDeleteExpires } from 'reactApp/utils/formatDate';
import { noop } from 'reactApp/utils/helpers';

import { Favorites } from './components/Favorites';
import { Pic } from './components/Pic';
import { Publish } from './components/Publish';
import styles from './DataListItemThumb.css';
import { DataListItemThumbProps } from './DataListItemThumb.types';

const getDownloadHint = (ext?: string, size?: number) =>
    ext
        ? `Скачать .${ext.toLowerCase()}${size ? `, ${bytesToNDigits(size, 3).value}` : ''}`
        : `Скачать${size ? ` ${bytesToNDigits(size, 3).value}` : ''}`;

// eslint-disable-next-line max-lines-per-function
export const DataListItemThumbComponent: VFC<DataListItemThumbProps> = (props) => {
    const {
        isSelected,
        isActive,
        isSelecting,
        forceCheckbox,
        onCheckboxClick,
        size,
        isFolder,
        name,
        ext,
        tipData,
        id,
        denyDoAction,
        progress,
        storage,
        isPublicDownloaded,
        isPopup,
    } = props;

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const config = STORAGE_CONFIG[props.storage];

    const handleDropdown = useCallback(
        (isOpen) => {
            if (denyDoAction) {
                return null;
            }

            setDropdownOpen(isOpen);
        },
        [setDropdownOpen, denyDoAction]
    );

    const renderFooter = () => {
        if (isPopup) {
            return null;
        }

        switch (storage) {
            case EStorageType.home:
            case EStorageType.sharedLinks:
            case EStorageType.sharedAutodelete:
            case EStorageType.favorites:
            case EStorageType.search:
            case EStorageType.feed:
            case EStorageType.documents:
            case EStorageType.gallery:
                if (storage === EStorageType.home && denyDoAction) {
                    return <Progress className={styles.progress} progress={progress} onClick={props.onCancelUpload} />;
                }

                return (
                    <Hint text="Поделиться ссылкой">
                        <div>
                            <Publish isActive={props.isPublic} onClick={props.onPublish} />
                        </div>
                    </Hint>
                );
            case EStorageType.attaches:
            case EStorageType.public:
                return (
                    props.owner && (
                        <Hint text={props.owner.name || props.owner.email}>
                            <div className={styles.owner}>
                                <Avatar email={props.owner.email} name={props.owner.name} size={30} />
                            </div>
                        </Hint>
                    )
                );
        }
    };

    const renderFavorites = () => {
        const isFavorites = storage === EStorageType.favorites;
        const isFavoritesEnable = config.favorites && 'favoritesAvailable' in props && props.favoritesAvailable;

        if (denyDoAction || !isFavoritesEnable || isPopup) {
            return null;
        }

        return (
            <Favorites
                isActive={'isInFavorites' in props && Boolean(props.isInFavorites)}
                onClick={'onFavorites' in props ? props.onFavorites : noop}
                isActiveHidden={isFavorites}
            />
        );
    };

    const renderPublicExpires = useMemo(() => {
        if (denyDoAction || isPopup) {
            return null;
        }

        const expires = 'publicExpires' in props && props.publicExpires;
        const isAutoDelete = 'isAutoDelete' in props && props.isAutoDelete;

        if (!!expires && isAutoDelete) {
            return <div className={styles.expires}>Удалится {formatAutoDeleteExpires(expires, true)}</div>;
        }
    }, [props]);

    const renderDownloadButton = () => {
        if (denyDoAction || forceCheckbox || isPopup || (storage === EStorageType.public && isPublicDownloaded)) {
            return null;
        }

        const downloadHint = getDownloadHint(ext, size);

        return ext && downloadAsJpeg.isAvailable({ ext, storage }) ? (
            <DataListControl
                type="downloadAsJpeg"
                onClick={props.onDownload}
                onDropdown={handleDropdown}
                list={downloadAsJpeg.getDropdownItems({
                    itemOrId: id,
                    gaSuffix: 'datalist',
                    ext,
                    textPrefix: 'в',
                    storage: props.storage,
                })}
                hint={downloadHint}
                viewType={EViewMode.thumbs}
            />
        ) : (
            <Hint text={downloadHint}>
                <div>
                    <DataListControl type="download" onClick={props.onDownload} viewType={EViewMode.thumbs} />
                </div>
            </Hint>
        );
    };

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.root_selected]: isSelected || isDropdownOpen,
                [styles.root_active]: isActive,
                [styles.root_selecting]: isSelecting,
            })}
        >
            {!isPopup && (
                <div className={styles.pinLeft}>
                    {!!tipData && (
                        <Tip
                            hideTip={tipData[0].onHideClick}
                            position="top center"
                            text={tipData[0].getTipContent?.(isFolder)}
                            tips={tipData}
                            id={tipData[0].id}
                            show
                            showBubble={false}
                            className={styles.pinLeftTooltip}
                        />
                    )}
                </div>
            )}
            <div className={styles.pin}>
                {renderDownloadButton()}
                {(forceCheckbox || isPopup) && (
                    <Hint text={isSelected ? 'Снять выделение' : 'Выделить'}>
                        <div onClick={onCheckboxClick}>
                            <DataListCheckbox checked={isSelected} />
                        </div>
                    </Hint>
                )}
            </div>
            <div className={styles.content}>
                <Pic
                    isFolder={props.isFolder}
                    thumbUrl={props.thumbUrl}
                    kind={props.kind}
                    isVirus={'isVirus' in props && Boolean(props.isVirus)}
                    isAutoDelete={'isAutoDelete' in props && props.isAutoDelete}
                    ext={props.ext || ''}
                    isLoading={denyDoAction}
                />
            </div>
            <div className={styles.footer}>
                <div className={styles.controls}>{renderFooter()}</div>
                <div className={styles.meta}>
                    <div className={styles.name}>
                        {renderFavorites()}
                        <NameComponent name={name} extension={ext} showTooltip />
                    </div>
                    {renderPublicExpires}
                </div>
            </div>
        </div>
    );
};

export const DataListItemThumb = memo(DataListItemThumbComponent);
DataListItemThumb.displayName = 'DataListItemThumb';
DataListItemThumb.whyDidYouRender = true;
